import { useEffect, useState } from "react";
import { Accessibility, Type, Contrast, Underline, ZapOff, RotateCcw, X } from "lucide-react";
import { Button } from "@/ui/base/button";
import { Switch } from "@/ui/base/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/base/popover";
import { cn } from "@/nucleo/utilitarios";

/**
 * Widget de acessibilidade flutuante para o portal público.
 *
 * Ajustes de apoio, aplicados por classe no <html> e guardados neste navegador.
 * NAO declara certificacao: o compromisso com a WCAG 2.1 AA e o canal para
 * relatar barreira ficam em /acessibilidade.
 *
 * Controles:
 *  - Tamanho da fonte (Padrão / Grande / Muito grande)
 *  - Alto contraste (override de tokens semânticos)
 *  - Redução de movimento (anula transitions/animations)
 *  - Sublinhar links
 *
 * Aplica classes utilitárias no <html> e persiste em localStorage.
 */

type FontSize = "default" | "large" | "xlarge";

interface A11yPrefs {
  fontSize: FontSize;
  highContrast: boolean;
  reduceMotion: boolean;
  underlineLinks: boolean;
}

const STORAGE_KEY = "teg-a11y-prefs";
const DEFAULT_PREFS: A11yPrefs = {
  fontSize: "default",
  highContrast: false,
  reduceMotion: false,
  underlineLinks: false,
};

function loadPrefs(): A11yPrefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<A11yPrefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function applyPrefs(prefs: A11yPrefs) {
  const html = document.documentElement;
  html.classList.toggle("a11y-font-lg", prefs.fontSize === "large");
  html.classList.toggle("a11y-font-xl", prefs.fontSize === "xlarge");
  html.classList.toggle("a11y-high-contrast", prefs.highContrast);
  html.classList.toggle("a11y-reduce-motion", prefs.reduceMotion);
  html.classList.toggle("a11y-underline-links", prefs.underlineLinks);
}

export function AcessibilidadeWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<A11yPrefs>(DEFAULT_PREFS);

  // Carrega 1x e aplica
  useEffect(() => {
    const initial = loadPrefs();
    setPrefs(initial);
    applyPrefs(initial);
  }, []);

  const update = (patch: Partial<A11yPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    applyPrefs(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore quota / SSR
    }
  };

  const reset = () => update(DEFAULT_PREFS);

  const fontOptions: { value: FontSize; label: string; sample: string }[] = [
    { value: "default", label: "Padrão", sample: "A" },
    { value: "large", label: "Grande", sample: "A" },
    { value: "xlarge", label: "Muito grande", sample: "A" },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label="Abrir opções de acessibilidade"
          aria-expanded={open}
          size="icon"
          // O marcador `data-widget-acessibilidade` e o que o CSS usa para subir
          // este botao enquanto o aviso de cookies ocupa a faixa inferior
          // (ui/estilos/index.css). z acima da faixa, para nunca ficar embaixo dela.
          data-widget-acessibilidade=""
          className="fixed bottom-4 right-4 z-70 h-12 w-12 rounded-full shadow-lg transition-[box-shadow,bottom] hover:shadow-xl focus-visible:ring-4"
        >
          <Accessibility className="h-6 w-6" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="top"
        className="w-80 p-0 z-60"
        sideOffset={12}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40">
          <div className="flex items-center gap-2">
            <Accessibility className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Acessibilidade</h3>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Fechar"
            className="rounded p-1 hover:bg-muted focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Tamanho da fonte */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Type className="h-3.5 w-3.5 text-muted-foreground" />
              <span
                id="a11y-tamanho-fonte"
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                Tamanho da fonte
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1.5" role="group" aria-labelledby="a11y-tamanho-fonte">
              {fontOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => update({ fontSize: opt.value })}
                  aria-pressed={prefs.fontSize === opt.value}
                  className={cn(
                    "rounded-lg border-2 px-2 py-2 text-center transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                    prefs.fontSize === opt.value
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <div
                    className={cn(
                      "font-bold leading-none",
                      opt.value === "default" && "text-base",
                      opt.value === "large" && "text-lg",
                      opt.value === "xlarge" && "text-2xl"
                    )}
                  >
                    {opt.sample}
                  </div>
                  <div className="text-[10px] mt-1 text-muted-foreground">{opt.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Alto contraste */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <Contrast className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <label htmlFor="a11y-contrast" className="text-sm font-medium cursor-pointer">
                  Alto contraste
                </label>
                <p className="text-xs text-muted-foreground">Preto e amarelo</p>
              </div>
            </div>
            <Switch
              id="a11y-contrast"
              checked={prefs.highContrast}
              onCheckedChange={(v) => update({ highContrast: v })}
            />
          </div>

          {/* Sublinhar links */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <Underline className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <label htmlFor="a11y-underline" className="text-sm font-medium cursor-pointer">
                  Sublinhar links
                </label>
                <p className="text-xs text-muted-foreground">Destaca todos os links</p>
              </div>
            </div>
            <Switch
              id="a11y-underline"
              checked={prefs.underlineLinks}
              onCheckedChange={(v) => update({ underlineLinks: v })}
            />
          </div>

          {/* Reduzir movimento */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <ZapOff className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <label htmlFor="a11y-motion" className="text-sm font-medium cursor-pointer">
                  Reduzir movimento
                </label>
                <p className="text-xs text-muted-foreground">Desativa animações</p>
              </div>
            </div>
            <Switch
              id="a11y-motion"
              checked={prefs.reduceMotion}
              onCheckedChange={(v) => update({ reduceMotion: v })}
            />
          </div>

          {/* Reset */}
          <div className="pt-2 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              onClick={reset}
              className="w-full justify-center"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-2" />
              Restaurar padrão
            </Button>
          </div>

          {/*
            Aqui havia "Conformidade WCAG 2.1 AA · Selo CPA". O Selo CPA e
            certificacao concedida pela Comissao Permanente de Acessibilidade da
            Prefeitura, apos auditoria — afirmar que se tem, sem ter, e declaracao
            falsa em portal de governo. O que se pode dizer e o compromisso, e ele
            fica na declaracao de acessibilidade, com o canal para relatar
            barreira.
          */}
          <p className="text-[11px] text-muted-foreground text-center pt-1">
            Preferências salvas neste navegador
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
